import React, { useState, useMemo, useEffect } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { conflictManager } from '../services/ConflictManager';

const ConflictListModal = ({
    isOpen,
    onClose,
    conflicts,
    currentWeekNumber,
    currentYear
}) => {
    const [filterText, setFilterText] = useState('');
    const [showDismissed, setShowDismissed] = useState(false);
    const [dismissedVersion, setDismissedVersion] = useState(0);
    const [typeMenuOpen, setTypeMenuOpen] = useState(false);
    const [typeFilters, setTypeFilters] = useState({});

    useEffect(() => {
        conflictManager.cleanupDismissedConflicts();
        const unsubscribe = conflictManager.onDismissedChange(() => {
            setDismissedVersion(v => v + 1);
        });
        return () => unsubscribe();
    }, []);

    const getReasonType = (reasonText, reason = null) => {
        if (reason?.isCrossPeriod) return 'Cross-period';
        const lower = (reasonText || '').toLowerCase();
        if (lower.includes('holiday')) return 'Holiday';
        if (lower.includes('room') && lower.includes('occupied')) return 'Room occupied';
        if (lower.includes('room') && lower.includes('not suitable')) return 'Room not suitable';
        if (lower.includes('teacher') && lower.includes('unavailable')) return 'Teacher unavailable';
        if (lower.includes('teacher') && lower.includes('prefers away')) return 'Teacher prefers away';
        if (lower.includes('teacher') && lower.includes('conflicting class')) return 'Teacher conflict';
        if (lower.includes('group') && lower.includes('unavailable')) return 'Group unavailable';
        if (lower.includes('group') && lower.includes('prefers away')) return 'Group prefers away';
        if (lower.includes('group') && lower.includes('conflicting class')) return 'Group conflict';
        if (lower.includes('invalid input')) return 'Data issue';
        return 'Other';
    };

    // Group conflicts by schedule item and combine their reasons with week numbers
    const groupedConflicts = useMemo(() => {
        const groups = new Map();

        conflicts.forEach(conflict => {
            const key = `${conflict.itemId}-${conflict.day}-${conflict.time}-${conflict.room}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    ...conflict,
                    reasonGroups: new Map() // Map of base reason -> Set of weeks
                });
            }

            const group = groups.get(key);

            conflict.reasons.forEach(reason => {
                // Extract the base message without the week number
                const baseMessage = reason.reason.replace(/, week \d+/i, '').replace(/\(week \d+\)/i, '').trim();
                const reasonId = conflictManager.makeConflictId(conflict.itemId, baseMessage);
                const weekMatch = reason.reason.match(/week (\d+)/i);
                const weekNumber = weekMatch ? parseInt(weekMatch[1], 10) : null;

                if (!group.reasonGroups.has(baseMessage)) {
                    group.reasonGroups.set(baseMessage, {
                        weeks: new Set(),
                        severity: reason.severity,
                        dismissed: conflictManager.isConflictDismissed(reasonId),
                        reasonId,
                        isCrossPeriod: Boolean(reason.isCrossPeriod),
                        otherPeriodName: reason.otherPeriodName || null
                    });
                }

                const reasonGroup = group.reasonGroups.get(baseMessage);
                if (reason.isCrossPeriod) {
                    reasonGroup.isCrossPeriod = true;
                    if (reason.otherPeriodName) {
                        reasonGroup.otherPeriodName = reason.otherPeriodName;
                    }
                }

                if (weekNumber) {
                    group.reasonGroups.get(baseMessage).weeks.add(weekNumber);
                }
            });
        });

        // Convert back to array and format reasons
        return Array.from(groups.values()).map(group => {
            const formattedReasons = Array.from(group.reasonGroups.entries()).map(([baseMessage, data]) => {
                const weeks = Array.from(data.weeks).sort((a, b) => a - b);
                const weekText = weeks.length > 0 ? ` (weeks ${weeks.join(', ')})` : '';
                const reasonText = `${baseMessage}${weekText}`;
                return {
                    reason: reasonText,
                    severity: data.severity,
                    dismissed: data.dismissed,
                    reasonId: data.reasonId,
                    type: getReasonType(reasonText, data),
                    isCrossPeriod: data.isCrossPeriod,
                    otherPeriodName: data.otherPeriodName
                };
            });

            return {
                ...group,
                reasons: formattedReasons,
                allWeeks: new Set(Array.from(group.reasonGroups.values()).flatMap(data => Array.from(data.weeks)))
            };
        }).filter(group => group.reasons.length > 0);
    }, [conflicts, dismissedVersion]);

    const normalizedFilter = filterText.trim().toLowerCase();

    // Collect available conflict types
    const availableTypes = useMemo(() => {
        const types = new Set();
        groupedConflicts.forEach(conflict => {
            conflict.reasons.forEach(r => types.add(r.type || 'Other'));
        });
        return Array.from(types);
    }, [groupedConflicts]);

    const allSelected = useMemo(() => {
        if (availableTypes.length === 0) return false;
        return availableTypes.every(t => typeFilters[t] !== false);
    }, [availableTypes, typeFilters]);

    const anySelected = useMemo(() => {
        return availableTypes.some(t => typeFilters[t] !== false);
    }, [availableTypes, typeFilters]);

    // Ensure type filters include all types (default on)
    useEffect(() => {
        setTypeFilters(prev => {
            const next = { ...prev };
            let changed = false;
            availableTypes.forEach(t => {
                if (next[t] === undefined) {
                    next[t] = true;
                    changed = true;
                }
            });
            // remove missing types
            Object.keys(next).forEach(t => {
                if (!availableTypes.includes(t)) {
                    delete next[t];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [availableTypes]);

    // Filter conflicts by text (subject or conflict reason)
    const filteredConflicts = useMemo(() => {
        if (!normalizedFilter) return groupedConflicts;
        return groupedConflicts.filter(conflict => {
            const subjectMatch = (conflict.itemName || '').toLowerCase().includes(normalizedFilter);
            const reasonMatch = (conflict.reasons || []).some(r =>
                (r.reason || '').toLowerCase().includes(normalizedFilter)
            );
            return subjectMatch || reasonMatch;
        });
    }, [groupedConflicts, normalizedFilter]);

    // Show either active conflicts or only dismissed conflicts
    const visibleConflicts = useMemo(() => {
        return filteredConflicts
            .map(conflict => {
                const reasons = (showDismissed
                    ? conflict.reasons.filter(r => r.dismissed)
                    : conflict.reasons.filter(r => !r.dismissed))
                    .filter(r => typeFilters[r.type ?? 'Other'] !== false);
                return { ...conflict, reasons };
            })
            .filter(conflict => conflict.reasons.length > 0);
    }, [filteredConflicts, showDismissed, typeFilters]);

    // Get unique weeks from all conflicts for display
    const allWeeks = useMemo(() => {
        const weeks = new Set();
        groupedConflicts.forEach(conflict => {
            conflict.allWeeks.forEach(week => {
                weeks.add(week);
            });
        });
        return Array.from(weeks).sort((a, b) => a - b);
    }, [groupedConflicts]);

    const dismissedCount = useMemo(() => {
        return groupedConflicts.reduce((sum, conflict) => {
            return sum + conflict.reasons.filter(r => r.dismissed).length;
        }, 0);
    }, [groupedConflicts]);

    const activeCount = useMemo(() => {
        return groupedConflicts.reduce((sum, conflict) => {
            return sum + conflict.reasons.filter(r => !r.dismissed).length;
        }, 0);
    }, [groupedConflicts]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Schedule Conflicts (${visibleConflicts.length})`}
        >
            <div className="flex flex-col h-[72vh]">
                {/* Filter Controls */}
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="Filter by subject or conflict text"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-grey-100"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                            onClick={() => setTypeMenuOpen(v => !v)}
                            aria-label="Filter by conflict type"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M10 18h4" />
                            </svg>
                        </button>
                        {typeMenuOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                <div className="p-2 text-xs text-gray-500 font-semibold">Conflict types</div>
                                <div className="max-h-56 overflow-y-auto">
                                    {availableTypes.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-500">No types available</div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 ${allSelected ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                onClick={() => {
                                                    const toggleTo = !allSelected;
                                                    const next = availableTypes.reduce((acc, t) => {
                                                        acc[t] = toggleTo;
                                                        return acc;
                                                    }, {});
                                                    setTypeFilters(next);
                                                }}
                                                aria-pressed={allSelected}
                                            >
                                                <span>All</span>
                                                <span className={`w-4 h-4 rounded border ${allSelected ? 'bg-blue-500 border-blue-500' : anySelected ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}></span>
                                            </button>
                                            {availableTypes.map((type) => (
                                                <label key={type} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50">
                                                    <span className="text-gray-700">{type}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={typeFilters[type] !== false}
                                                        onChange={() =>
                                                            setTypeFilters(prev => ({
                                                                ...prev,
                                                                [type]: !(prev[type] !== false)
                                                            }))
                                                        }
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                </label>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant={showDismissed ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setShowDismissed(v => !v)}
                            disabled={!dismissedCount && !showDismissed}
                            aria-label={showDismissed ? 'Show active conflicts' : 'Show dismissed conflicts only'}
                        >
                            {showDismissed ? 'Show active' : `Show dismissed${dismissedCount ? ` (${dismissedCount})` : ''}`}
                        </Button>
                    </div>
                </div>

                {/* Dismiss all CTA */}
                <div className="flex justify-end mb-2">
                    <button
                        type="button"
                        className="text-xs text-blue-500 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                        onClick={() => {
                            groupedConflicts.forEach(conflict => {
                                conflict.reasons.forEach(r => {
                                    if (r.reasonId && !r.dismissed) {
                                        conflictManager.toggleConflictDismissed(r.reasonId);
                                    }
                                });
                            });
                            setDismissedVersion(v => v + 1);
                        }}
                        disabled={activeCount === 0}
                        aria-label="Dismiss all conflicts"
                    >
                        Dismiss all
                    </button>
                </div>

                {/* Conflict List */}
                <div className="flex-1 overflow-y-auto">
                    {visibleConflicts.length > 0 ? (
                        <ul className="space-y-3 text-sm">
                            {visibleConflicts.map((conflict, index) => (
                                <li key={`${conflict.itemId}-${index}`} className="border-b pb-2 last:border-b-0">
                                    <p className="font-semibold text-gray-800 capitalize">
                                        {conflict.itemName} ({conflict.day} {conflict.time} @ {conflict.room})
                                    </p>
                                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                        {conflict.reasons.map((reason, rIndex) => {
                                            // Highlight current week conflicts
                                            const isCurrentWeek = conflict.allWeeks.has(currentWeekNumber);
                                            const baseColorClass = reason.severity === 'red' ? 'text-red-600' : 'text-orange-600';
                                            const highlightClass = isCurrentWeek ? 'font-medium px-1 rounded' : '';

                                            return (
                                                <li key={rIndex} className={`${baseColorClass}`}>
                                                    <div className={`flex items-start justify-between gap-3 ${highlightClass}`}>
                                                        <div className={`flex flex-col gap-1 min-w-0 ${reason.dismissed ? 'line-through text-gray-400' : ''}`}>
                                                            <span>{reason.reason}</span>
                                                            {reason.isCrossPeriod && reason.otherPeriodName && (
                                                                <span className="inline-flex w-fit items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
                                                                    Other period · {reason.otherPeriodName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                conflictManager.toggleConflictDismissed(reason.reasonId);
                                                                setDismissedVersion(v => v + 1);
                                                            }}
                                                            className={`text-xs px-2 py-0.5 rounded ${reason.dismissed ? 'border border-gray-300 text-gray-600 hover:bg-gray-100' : 'text-blue-400 hover:text-blue-600'}`}
                                                            aria-label={reason.dismissed ? 'Restore conflict' : 'Dismiss conflict'}
                                                        >
                                                            {reason.dismissed ? 'Restore' : 'Dismiss'}
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-8">
                            {showDismissed ? (
                                <p className="text-gray-500">No dismissed conflicts.</p>
                            ) : normalizedFilter ? (
                                <p className="text-gray-500">No conflicts match that filter.</p>
                            ) : (
                                <p className="text-gray-500">No conflicts detected in the current schedule.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                        {showDismissed ? (
                            `Dismissed conflicts: ${visibleConflicts.length || 0}`
                        ) : (
                            `Total conflicts: ${visibleConflicts.length}${dismissedCount && !showDismissed ? ` (hiding ${dismissedCount} dismissed)` : ''}`
                        )}
                    </div>
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ConflictListModal; 